import React, { useState, useEffect, useMemo } from 'react';
import { db, auth } from '../../firebase';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { Map, AdvancedMarker, InfoWindow, useMap } from '@vis.gl/react-google-maps';
import { useTranslation } from '../../context/LanguageContext';
import {
    FiAward, FiCalendar, FiMapPin, FiUsers,
    FiChevronRight, FiChevronLeft, FiUserCheck, FiUser
} from 'react-icons/fi';

const pageContainerStyle = {
    padding: '2rem',
    fontFamily: 'Arial, sans-serif',
    backgroundColor: '#f0f2f5',
    minHeight: 'calc(100vh - 4rem)',
    maxWidth: '1200px',
    margin: '0 auto'
};

const headingStyle = {
    color: '#b30000',
    textAlign: 'center',
    marginBottom: '1.5rem'
};

const mapContainerStyle = {
    width: '100%',
    height: '350px',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    marginBottom: '2rem'
};

const weekNavigatorStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#fff',
    padding: '12px 20px',
    borderRadius: '10px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    marginBottom: '20px'
};

const navBtnStyle = {
    backgroundColor: '#b30000',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontWeight: 'bold',
    fontSize: '0.9rem',
    transition: 'background 0.2s'
};

const weekTextStyle = {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    color: '#333'
};

const cardStyle = {
    display: 'flex',
    gap: '20px',
    border: '1px solid #ddd',
    borderRadius: '12px',
    padding: '16px',
    margin: '16px 0',
    boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
    backgroundColor: '#fff',
    minHeight: '100px',
    cursor: 'pointer',
    position: 'relative'
};

const leftColumnStyle = {
    flex: '1 1 100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
};

const titleStyle = {
    color: '#b30000',
    marginTop: '0',
    marginBottom: '0',
    fontSize: '1.25rem',
    fontWeight: 'bold'
};

const detailStyle = {
    fontSize: '0.9rem',
    color: '#444',
    margin: '0',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
};

const getCompetitionTypeDisplay = (rawType) => {
    const type = rawType?.toLowerCase() || '';
    if (type.includes('3d')) return { label: '3D', color: '#28a745' };
    if (type.includes('palya')) return { label: 'Pálya', color: '#007bff' };
    if (type.includes('orom')) return { label: 'Örömijász', color: '#ffc107' };
    if (type.includes('tortenelmi')) return { label: 'Történelmi', color: '#dc3545' };
    if (type.includes('terep')) return { label: 'Terep', color: '#6d4c41' };
    return { label: rawType || 'Egyéb', color: '#9c27b0' };
};

// --- ÚJ: KLUB NÉV FORMÁZÓ SEGÉDFÜGGVÉNY ---
const formatClubName = (name) => {
  if (!name || name === "Egyéb" || name === "N/A" || name === "Ismeretlen") return name;
  const specialCases = {
      "3d": "3D", "kft.": "Kft.", "se": "SE", "sk": "SK", "dse": "DSE",
      "kse": "KSE", "mavir": "MAVIR", "ifaa": "IFAA", "hdh-iaa": "HDH-IAA"
  };

  return name.split(' ').map(word => {
    const cleanWord = word.replace(/[^a-zA-ZáéíóöőúüűÁÉÍÓÖŐÚÜŰ0-9]/g, '').toLowerCase();
    if (specialCases[cleanWord]) return specialCases[cleanWord];
    if (word.startsWith('"')) {
        return '"' + (word.charAt(1)?.toUpperCase() || "") + word.slice(2).toLowerCase();
    }
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }).join(' ');
};

const ResultsListPage = () => {
    const { locale, t } = useTranslation();
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentUser, setCurrentUser] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [selectedMarker, setSelectedMarker] = useState(null);
    const map = useMap();
    const navigate = useNavigate();

    const [isMonthlyView, setIsMonthlyView] = useState(false);
    const [currentWeekStart, setCurrentWeekStart] = useState(() => {
        const savedWeek = sessionStorage.getItem('last_viewed_results_week');
        if (savedWeek) return new Date(savedWeek);
        const d = new Date();
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1);
        return new Date(d.setDate(diff));
    });

    useEffect(() => {
        sessionStorage.setItem('last_viewed_results_week', currentWeekStart.toISOString());
    }, [currentWeekStart]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);

        fetchClosedCompetitions();

        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setCurrentUser(user);
        });
        return () => {
            unsubscribe();
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const fetchClosedCompetitions = async () => {
        setLoading(true);
        try {
            const q = query(
                collection(db, "events_closed")
            );
            const querySnapshot = await getDocs(q);
            const data = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setResults(data);
        } catch (err) {
            console.error("Error fetching results:", err);
        } finally {
            setLoading(false);
        }
    };

    const changePeriod = (direction) => {
        setCurrentWeekStart(prev => {
            const newDate = new Date(prev);
            if (isMonthlyView) {
                newDate.setMonth(newDate.getMonth() + direction);
            } else {
                newDate.setDate(newDate.getDate() + (direction * 7));
            }
            return newDate;
        });
    };

    const dateDisplayString = useMemo(() => {
        const dateLocale = locale === 'en' ? 'en-US' : 'hu-HU';
        if (isMonthlyView) {
            const d = new Date(currentWeekStart);
            const monthName = d.toLocaleDateString(dateLocale, { year: 'numeric', month: 'long' });
            return monthName.charAt(0).toUpperCase() + monthName.slice(1);
        } else {
            const start = new Date(currentWeekStart);
            const end = new Date(currentWeekStart);
            end.setDate(end.getDate() + 6);

            const startYear = start.getFullYear();
            const endYear = end.getFullYear();
            const startMonth = start.getMonth();
            const endMonth = end.getMonth();

            if (startYear === endYear) {
                if (startMonth === endMonth) {
                    const startStr = start.toLocaleDateString(dateLocale, { year: 'numeric', month: 'short', day: 'numeric' });
                    const endDayStr = end.getDate() + '.';
                    return `${startStr} - ${endDayStr}`;
                } else {
                    const startStr = start.toLocaleDateString(dateLocale, { year: 'numeric', month: 'short', day: 'numeric' });
                    const endMonthDayStr = end.toLocaleDateString(dateLocale, { month: 'short', day: 'numeric' });
                    return `${startStr} - ${endMonthDayStr}`;
                }
            } else {
                const startStr = start.toLocaleDateString(dateLocale, { year: 'numeric', month: 'short', day: 'numeric' });
                const endStr = end.toLocaleDateString(dateLocale, { year: 'numeric', month: 'short', day: 'numeric' });
                return `${startStr} - ${endStr}`;
            }
        }
    }, [currentWeekStart, isMonthlyView, locale]);

    const filteredResults = useMemo(() => {
        const startMs = new Date(currentWeekStart);
        const endMs = new Date(currentWeekStart);

        if (isMonthlyView) {
            startMs.setDate(1);
            startMs.setHours(0,0,0,0);

            endMs.setMonth(endMs.getMonth() + 1);
            endMs.setDate(0);
            endMs.setHours(23,59,59,999);
        } else {
            startMs.setHours(0,0,0,0);
            endMs.setDate(endMs.getDate() + 6);
            endMs.setHours(23,59,59,999);
        }

        return results
            .filter(event => {
                const eventDateStr = event.competitionDate || event.startDate;
                if (!eventDateStr) return false;
                const eventTime = new Date(eventDateStr).getTime();
                return eventTime >= startMs.getTime() && eventTime <= endMs.getTime();
            })
            .sort((a, b) => {
                const dateA = a.competitionDate || a.startDate || "";
                const dateB = b.competitionDate || b.startDate || "";
                return dateB.localeCompare(dateA);
            });
    }, [results, currentWeekStart, isMonthlyView]);

    useEffect(() => {
        if (!map) return;

        if (filteredResults.length === 0) {
            map.setCenter({ lat: 47.1625, lng: 19.5033 });
            map.setZoom(7);
            return;
        }

        const bounds = new window.google.maps.LatLngBounds();
        let hasCoords = false;

        filteredResults.forEach(event => {
            const lat = parseFloat(event.gpsLatitude);
            const lng = parseFloat(event.gpsLongitude);
            if (!isNaN(lat) && !isNaN(lng)) {
                bounds.extend({ lat, lng });
                hasCoords = true;
            }
        });

        if (hasCoords) {
            map.fitBounds(bounds, {
                top: 50,
                right: 50,
                bottom: 50,
                left: 50
            });

            if (filteredResults.length === 1) {
                const listener = window.google.maps.event.addListener(map, "idle", () => {
                    if (map.getZoom() > 12) map.setZoom(12);
                    window.google.maps.event.removeListener(listener);
                });
            }
        }
    }, [filteredResults, map]);

    if (loading) return <div style={{textAlign: 'center', padding: '50px'}}>{t('results.loading')}</div>;

    const dateLocale = locale === 'en' ? 'en-US' : 'hu-HU';

    return (
        <div className="force-light-theme" style={{ ...pageContainerStyle, padding: isMobile ? '1rem' : '2rem' }}>
            {!isMobile && <h1 style={{ ...headingStyle, fontSize: '2.5rem' }}>{t('results.title')}</h1>}

            {/* TÉRKÉP */}
            <div style={{ ...mapContainerStyle, height: isMobile ? '250px' : '350px' }}>
                <Map
                    defaultCenter={{ lat: 47.1625, lng: 19.5033 }}
                    defaultZoom={7}
                    mapId="RESULTS_MAP_ID"
                    style={{ width: '100%', height: '100%' }}
                    disableDefaultUI={true}
                    gestureHandling={'greedy'}
                >
                    {filteredResults.map(event => {
                        const lat = parseFloat(event.gpsLatitude);
                        const lng = parseFloat(event.gpsLongitude);
                        if (!isNaN(lat) && !isNaN(lng)) {
                            const typeDisplay = getCompetitionTypeDisplay(event.competitionType);
                            return (
                                <AdvancedMarker
                                    key={event.id}
                                    position={{ lat, lng }}
                                    title={event.competitionName}
                                    onClick={() => setSelectedMarker(event)}
                                >
                                    <div style={{
                                        width: '18px', height: '18px',
                                        backgroundColor: typeDisplay.color,
                                        borderRadius: '50%', border: '3px solid white',
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                                        transform: 'translate(-50%, -50%)',
                                        cursor: 'pointer'
                                    }} />
                                </AdvancedMarker>
                            );
                        }
                        return null;
                    })}

                    {selectedMarker && (
                        <InfoWindow
                            position={{
                                lat: parseFloat(selectedMarker.gpsLatitude),
                                lng: parseFloat(selectedMarker.gpsLongitude)
                            }}
                            onCloseClick={() => setSelectedMarker(null)}
                            pixelOffset={[0, -10]}
                        >
                            <div
                                style={{
                                    padding: '4px',
                                    textAlign: 'center',
                                    maxWidth: '200px',
                                    cursor: 'pointer'
                                }}
                                onClick={() => navigate(`/eredmeny/${selectedMarker.id}`)}
                            >
                                <div style={{
                                    fontWeight: 'bold',
                                    color: '#D32F2F',
                                    fontSize: '0.95rem',
                                    marginBottom: '4px',
                                    lineHeight: '1.2'
                                }}>
                                    {selectedMarker.competitionName}
                                </div>
                                <div style={{
                                    fontSize: '0.8rem',
                                    color: '#555'
                                }}>
                                    {selectedMarker.location ? selectedMarker.location.split(',')[0].trim() : 'Ismeretlen helyszín'}
                                </div>
                                <div style={{
                                    fontSize: '0.75rem',
                                    color: '#b30000',
                                    fontWeight: 'bold',
                                    marginTop: '4px'
                                }}>
                                    Eredmények megtekintése ➔
                                </div>
                            </div>
                        </InfoWindow>
                    )}
                </Map>
            </div>

            {/* HETI / HAVI NAVIGÁTOR */}
            <div style={{ ...weekNavigatorStyle, padding: isMobile ? '8px' : '12px 20px', gap: '8px' }}>
                <button
                    style={{
                        backgroundColor: '#e9ecef',
                        color: '#b30000',
                        border: '1px solid #ced4da',
                        borderRadius: '10px',
                        padding: isMobile ? '8px' : '10px 14px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        fontSize: '1rem',
                        transition: 'all 0.2s ease'
                    }}
                    onClick={() => changePeriod(-1)}
                    title="Előző"
                >
                    <FiChevronLeft size={22} color="#b30000" />
                </button>

                <button
                    onClick={() => setIsMonthlyView(prev => !prev)}
                    style={{
                        backgroundColor: '#b30000',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '12px',
                        padding: isMobile ? '8px 12px' : '10px 20px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        fontWeight: 'bold',
                        fontSize: isMobile ? '0.85rem' : '1.05rem',
                        boxShadow: '0 2px 8px rgba(179, 0, 0, 0.25)',
                        flex: '1',
                        maxWidth: '400px',
                        whiteSpace: 'nowrap',
                        margin: '0 auto',
                        transition: 'all 0.2s ease'
                    }}
                    title="Kattints a Heti / Havi nézet váltáshoz!"
                >
                    <span>{dateDisplayString}</span>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="white" style={{ flexShrink: 0 }}>
                        <path d="M9 11.24V7.5C9 6.12 10.12 5 11.5 5S14 6.12 14 7.5v3.74c1.21-0.81 2-2.18 2-3.74C16 5.01 13.99 3 11.5 3S7 5.01 7 7.5c0 1.56 0.79 2.93 2 3.74zM18.84 15.87l-4.54-2.26c-0.17-0.07-0.35-0.11-0.54-0.11H13v-6C13 6.67 12.33 6 11.5 6S10 6.67 10 7.5v10.74l-3.44-0.72c-0.05-0.01-0.11-0.02-0.17-0.02-0.26 0-0.49 0.1-0.67 0.28l-0.72 0.72 5.06 5.06c0.35 0.35 0.83 0.54 1.33 0.54h6.88c0.75 0 1.36-0.54 1.48-1.27l0.96-5.56c0.06-0.31-0.01-0.63-0.2-0.89z" />
                    </svg>
                </button>

                <button
                    style={{
                        backgroundColor: '#e9ecef',
                        color: '#b30000',
                        border: '1px solid #ced4da',
                        borderRadius: '10px',
                        padding: isMobile ? '8px' : '10px 14px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        fontSize: '1rem',
                        transition: 'all 0.2s ease'
                    }}
                    onClick={() => changePeriod(1)}
                    title="Következő"
                >
                    <FiChevronRight size={22} color="#b30000" />
                </button>
            </div>

            {/* LISTA */}
            {filteredResults.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666', background: '#fff', borderRadius: '10px' }}>
                    <p>{t('results.noResultsThisWeek')}</p>
                </div>
            ) : (
                filteredResults.map(event => {
                    const typeDisplay = getCompetitionTypeDisplay(event.competitionType);
                    return (
                        <div key={event.id} style={cardStyle} onClick={() => navigate(`/eredmeny/${event.id}`)}>
                            {event.organizingClubLogoUrl && (
                                <div style={{
                                    position: 'absolute', top: '16px', right: '16px',
                                    width: '36px', height: '36px', borderRadius: '50%',
                                    border: '2px solid white', boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                                    overflow: 'hidden', backgroundColor: '#fff', zIndex: 2
                                }}>
                                    <img src={event.organizingClubLogoUrl} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </div>
                            )}

                            <div style={leftColumnStyle}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                    <h2 style={{ ...titleStyle, paddingRight: '45px' }}>{event.competitionName}</h2>
                                </div>

                                <p style={detailStyle}>
                                    <strong>Type:</strong>
                                    <span style={{ color: typeDisplay.color, marginLeft: '5px', fontWeight: 'bold' }}>{typeDisplay.label}</span>
                                </p>

                                <div style={{ color: '#666', fontSize: '0.85rem', opacity: 0.8, display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                                    <FiUsers size={14} />
                                    {t('results.organizer')} {formatClubName(event.organizingClub) || 'N/A'}
                                </div>

                                <p style={{ ...detailStyle, color: '#b30000', fontWeight: 'bold', marginTop: '4px' }}>
                                    <FiUsers size={14} /> {t('results.participants')} {event.finalResultCount || 0}
                                </p>

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px', flexWrap: 'wrap', gap: '10px' }}>
                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', fontSize: '0.85rem' }}>
                                        <span style={{ fontStyle: 'italic', color: '#444' }}>
                                            <FiCalendar size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                                            {event.competitionDate ? new Date(event.competitionDate).toLocaleDateString(dateLocale, { year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'long' }) : 'N/A'}
                                        </span>
                                        <span style={{ fontWeight: 'bold', color: '#D32F2F' }}>
                                            <FiMapPin size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                                            {event.location ? event.location.split(',')[0].trim() : 'N/A'}
                                        </span>
                                    </div>

                                    <div style={{ color: '#28a745', display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'bold', fontSize: '0.75rem' }}>
                                        <FiAward size={18} /> {t('results.finalResult')}
                                    </div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', marginLeft: '10px' }}>
                                <FiChevronRight size={24} color="#ccc" />
                            </div>
                        </div>
                    );
                })
            )}
            <div style={{ height: '80px' }} />
        </div>
    );
};

export default ResultsListPage;
